import React, { Component } from 'react'
import { ApolloProvider } from 'react-apollo'

import { ModalActionTypes } from '@acorns/web-components'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { ApolloClient } from 'apollo-client'
import { ApolloLink, Observable, from } from 'apollo-link'
import { BatchHttpLink } from 'apollo-link-batch-http'
import DebounceLink from 'apollo-link-debounce'
import { onError } from 'apollo-link-error'
import { path } from 'ramda'
import { compose } from 'recompose'
import UAParser from 'ua-parser-js'

import Environment, { withEnv } from 'utils/environment'
import { ErrorDetails, ErrorPayload, withApolloErrorModal } from 'utils/error'

type GetCustomHandlerType = (
  name: string,
) => (error: ErrorPayload) => ErrorDetails | boolean

type ProviderProps = {
  getCustomHandler: GetCustomHandlerType
  graphqlUrl: string
  env: Environment
  showErrorModal: (details: ErrorDetails) => void
  closeErrorModal: () => void
}

const DEBOUNCE_TIMEOUT = 250

export const hasServiceUnavailableError = (errors: any[] = []) =>
  Boolean(errors.find(({ code }) => code === 'service_unavailable'))

// Wrapping ApolloProvider so that apollo-link-error can access React props
export class ApolloProviderWrapper extends Component<ProviderProps, {}> {
  public render() {
    return (
      <ApolloProvider client={this.createClient()}>
        {this.props.children}
      </ApolloProvider>
    )
  }

  protected createClient() {
    // Note: Attaches some HTTP header on every network request
    const headersMiddleware = this.createHeadersMiddleware()

    // Note: All GraphQL and network errors get run through this
    // so we can take appropriate action
    const errorLink = this.createErrorLink()

    // Note: Performs debounce logic on queries that have a `debounceKey` configured
    const debounceLink = new DebounceLink(DEBOUNCE_TIMEOUT) as any

    // Note: Batch queries executed within an interval together
    // in a single network request
    const batchHttpLink = new BatchHttpLink({
      uri: this.props.graphqlUrl,
      credentials: 'include',
    })

    const links = [headersMiddleware, errorLink, debounceLink, batchHttpLink]

    return new ApolloClient({
      cache: new InMemoryCache(),
      link: from(links),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-first',
        },
      },
    })
  }

  protected createHeadersMiddleware() {
    return new ApolloLink((operation, forward) => {
      return new Observable((observer) => {
        const userAgentParser = new UAParser()
        const { browser, device, os } = userAgentParser.getResult()

        // Token needed for webviews on Mobile Apps. Only used if present.
        const customHeaders = {
          'X-Client-App': 'web-app',
          'X-Client-Browser-Version': browser.version,
          'X-Client-Browser': browser.name,
          'X-Client-Build': process.env.VERSION,
          'X-Client-Hardware': device.model,
          'X-Client-Os': os.name,
          'X-Client-Platform': 'web',
        }

        operation.setContext({
          headers: customHeaders,
        })

        return forward(operation).subscribe(observer)
      })
    })
  }

  protected errorLinkHandler({ graphQLErrors, networkError, operation }) {
    if (hasServiceUnavailableError(graphQLErrors)) {
      this.handleServiceUnavailableError()
    } else {
      this.handleError(graphQLErrors, networkError, operation)
    }
  }

  protected createErrorLink() {
    return onError(this.errorLinkHandler.bind(this))
  }

  protected handleAuthenticationError() {
    const errorModalHandle = () => {
      document.querySelector('[data-id]')
        ? this.props.closeErrorModal()
        : location.reload()
    }

    this.props.showErrorModal({
      title: 'Logged Out',
      body: "You've been logged out due to inactivity. Please log in to proceed.",
      actions: [
        {
          handler: errorModalHandle,
          label: 'Ok',
          type: ModalActionTypes.primary,
        },
      ],
    })
  }

  protected handleServiceUnavailableError() {
    this.props.showErrorModal({
      title: 'Please Contact Support',
      body: 'We are unable to process your request online, but our support team is here to help. Please contact us and we will work to resolve this as soon as possible.',
      actions: [
        {
          handler: this.props.closeErrorModal,
          label: 'Cancel',
          type: ModalActionTypes.secondary,
        },
        {
          href: 'https://help.acorns.com/contact',
          label: 'Contact Support',
          type: ModalActionTypes.primary,
        },
      ],
    })
  }

  protected handleError(graphQLErrors, networkError, operation) {
    const opertionType = path(
      ['query', 'definitions', '0', 'operation'],
      operation,
    )

    const handler = this.props.getCustomHandler(operation.operationName)

    let errorDetails

    if (typeof handler === 'function') {
      errorDetails = handler({
        graphQLErrors,
        networkError,
      })
    }

    return Promise.resolve(errorDetails).then((results) => {
      // Disable error modal if handler returns false
      if (typeof results === 'boolean' && !results) {
        return
      }

      if (opertionType === 'mutation' || results) {
        this.props.showErrorModal(results)
      }
    })
  }
}

const enhance = compose(withApolloErrorModal, withEnv)

export const Provider = enhance(ApolloProviderWrapper)
